import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Pagination,
} from "@mui/material";
import {
  PersonAddAlt1 as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { checkPermission } from "../../common/Sidebar/checkPermission";

const RolesList = ({
  roles,
  onAddRole,
  onEditRole,
  onDeleteRole,
  onSelectRole,
  disabledRoles,
  onBack,
}) => {
  const [page, setPage] = useState(1);
  const rowsPerPage = 15;
  const [rolesPermissions, setRolesPermissions] = useState({
    canView: false,
    canEdit: false,
    canDelete: false,
    canCreate: false,
  });
  useEffect(() => {
    const loadPermissions = async () => {
      const rolesPerms = await checkPermission( "utilities", "teams.rolesAndPermissions");
      setRolesPermissions(rolesPerms);
    };
    loadPermissions();
  }, []);
  const handleChangePage = (event, value) => setPage(value);
  const filteredRoles = roles.filter((role) => role.name !== "Super Admin");
  const sortedRoles = [...filteredRoles].sort((a, b) => {
    if (a.name === "Store Admin") return -1;
    if (b.name === "Store Admin") return 1;
    return 0;
  });
  const displayedRoles = sortedRoles.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );


  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      window.history.back();
    }
  };

  return (
    <Box sx={{ p: 1, bgcolor: "grey.50", minHeight: "100vh" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 3,
          justifyContent: "space-between",
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ color: "#0d3559" }}
        >
          Back
        </Button>
        { rolesPermissions.canCreate && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAddRole}
            sx={{
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
              textTransform: "none",
              backgroundColor: "#0d3559",
              "&:hover": { backgroundColor: "#0a2a45" },
              boxShadow: 2,
            }}
          >
            Add new role
          </Button>
        )}
      </Box>
      <Card sx={{ borderRadius: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Roles
            </Typography>
          </Box>
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              border: "1px solid #eee",
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.100" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                  {
                  (rolesPermissions.canEdit || rolesPermissions.canDelete) && 
                  <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                  }
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedRoles.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No roles found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {displayedRoles.map((role) => (
                  <TableRow
                    key={role._id}
                    hover
                    onClick={() => {
                      if (rolesPermissions.canEdit)
                        onSelectRole(role);
                    }}
                    sx={{
                      cursor: rolesPermissions.canEdit ? "pointer" : "not-allowed",
                      opacity: rolesPermissions.canEdit ? 1 : 0.5,
                      transition: "0.2s",
                    }}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>{role.name}</TableCell>
                    <TableCell>{role.description}</TableCell>
                    <TableCell>
                      {!disabledRoles.includes(role.name) && <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title="Edit Role">
                          { rolesPermissions.canEdit  &&
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditRole(role);
                            }}
                            disabled={
                              !rolesPermissions.canEdit
                            }
                            sx={{
                              borderRadius: 2,
                              px: 3,
                              fontWeight: 600,
                              textTransform: "none",
                              borderColor: "#0d3559",
                              color: "#0d3559",
                              "&:hover": {
                                borderColor: "#0a2a45",
                                backgroundColor: "rgba(13, 53, 89, 0.04)",
                              },
                              boxShadow: 2,
                            }}
                          >
                            Edit
                          </Button>
                          }
                        </Tooltip>
                        <Tooltip title="Delete Role">
                          { rolesPermissions.canDelete  &&
                          <Button
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteRole(role._id);
                            }}
                            startIcon={<DeleteIcon />}
                            sx={{
                              border: "1px solid",
                              borderRadius: 2,
                              px: 3,
                              fontWeight: 600,
                              textTransform: "none",
                              "&:hover": {
                                backgroundColor: "rgba(255, 0, 0, 0.04)",
                                borderColor: "error.dark",
                              },
                              boxShadow: 2,
                            }}
                          >
                            Delete
                          </Button>
                          }
                        </Tooltip>
                      </Box>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Pagination
              count={Math.ceil(filteredRoles.length / rowsPerPage)}
              page={page}
              onChange={handleChangePage}
              color="primary"
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default RolesList;
